import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { MenuItems } from "../../../utils/constants";
import { PATHS } from "../../../utils/paths";
import { ReactComponent as LogoLayout } from "../../../assets/layout/layout-logo.svg";
import { ReactComponent as SettingsIcon } from "../../../assets/layout/layout-settings.svg";
import { ReactComponent as SettingsIconActive } from "../../../assets/layout/layout-settings-active.svg";
import {
  BottomBlock,
  IconWrapper,
  MenuItem,
  MiddleBlock,
  NavBadge,
  SidebarBlock,
  TitleTooltip,
  TopBlock,
} from "./styled";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { logout } from "../../../api/Api.service";

// UX-04: simple inline log-out glyph (inherits the sidebar's currentColor) so we
// don't need a new SVG asset for the first logout control the app has ever had.
const LogoutIcon = () => (
  <svg
    className="sidebar-stroke-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const Sidebar = () => {
  const { t } = useTranslation();
  const menuItems = MenuItems(t);
  const [hoveredId, setHoveredId] = useState(null);
  const [settingsHover, setSettingsHover] = useState(false);
  // Live unread-conversations count pushed over /ws/user.
  const unreadCount = useSelector((state) => state.userUpdates.unreadChatCount);

  return (
    <SidebarBlock>
      <TopBlock>
        <NavLink to="/" aria-label={t("common.home")}>
          <IconWrapper $preserveColors>
            <LogoLayout />
          </IconWrapper>
        </NavLink>
      </TopBlock>

      {/* MIDDLE */}
      <MiddleBlock>
        {menuItems.map((item) => {
          return (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.path === "/"}
              aria-label={item.title}
            >
              {({ isActive }) => {
                const Icon =
                  isActive || hoveredId === item.id
                    ? item.activeIcon
                    : item.icon;

                return (
                  <MenuItem
                    $isActive={isActive}
                    onMouseEnter={() => setHoveredId(item.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <IconWrapper>
                      <Icon />
                      {item.path === PATHS.Conversations && unreadCount > 0 ? (
                        <NavBadge>
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </NavBadge>
                      ) : null}
                    </IconWrapper>

                    <TitleTooltip role="tooltip">{item.title}</TitleTooltip>
                  </MenuItem>
                );
              }}
            </NavLink>
          );
        })}
      </MiddleBlock>

      {/* BOTTOM */}
      <BottomBlock>
        <NavLink to={PATHS.Settings}>
          {({ isActive }) => {
            const Icon =
              isActive || settingsHover ? SettingsIconActive : SettingsIcon;

            return (
              <MenuItem
                $isActive={isActive}
                onMouseEnter={() => setSettingsHover(true)}
                onMouseLeave={() => setSettingsHover(false)}
              >
                <IconWrapper>
                  <Icon />
                </IconWrapper>

                <TitleTooltip role="tooltip">{t("SETTINGS")}</TitleTooltip>
              </MenuItem>
            );
          }}
        </NavLink>

        {/* UX-04: the app's only way to sign out (also clears token + userInfo). */}
        <MenuItem
          as="div"
          role="button"
          tabIndex={0}
          onClick={logout}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") logout();
          }}
          style={{ cursor: "pointer" }}
        >
          <IconWrapper>
            <LogoutIcon />
          </IconWrapper>
          <TitleTooltip role="tooltip">{t("LOG_OUT")}</TitleTooltip>
        </MenuItem>
      </BottomBlock>

      {/* <NavLink to="/">Home</NavLink>
      <NavLink to="/dashboard">Dashboard</NavLink> */}
    </SidebarBlock>
  );
};

export default Sidebar;
